/**
 * Shared user display formatting utilities.
 *
 * Identity model: the login identity is the email address; the stable
 * internal identity is the Supabase user UUID; the TEAM-FACING identity is
 * `display_name` — an editable, NON-unique label (not a username: no
 * uniqueness constraints or availability checks; email/role disambiguate
 * duplicates in member lists).
 *
 * Fallback chain (most preferred first):
 *   1. display_name (user_profiles column or user_metadata)
 *   2. Provider full_name (OAuth, e.g. Google)
 *   3. first + last name (legacy signups, invites)
 *   4. Generic name field
 *   5. Email address
 *   6. UUID short form
 */

/**
 * Resolve the editable display name from a user-like object, or '' when the
 * user has none set.
 *
 * @param {object|null} user
 * @returns {string}
 */
export function rawDisplayName(user) {
  if (!user) return '';
  const meta = user.user_metadata || {};
  return (
    (typeof user.display_name === 'string' && user.display_name.trim()) ||
    (typeof meta.display_name === 'string' && meta.display_name.trim()) ||
    ''
  );
}

/**
 * Build a full display name from a user-like object.
 *
 * Recognises the following shapes that appear in this codebase:
 *   - Supabase auth user  : { email, user_metadata: { display_name, first_name, last_name, full_name } }
 *   - user_profiles row   : { email, display_name, first_name, last_name }
 *   - localStorage blob   : { email, user_metadata: {...}, first_name?, last_name?, name? }
 *
 * @param {object|null} user
 * @returns {string}
 */
export function formatUserDisplayName(user) {
  if (!user) return 'Unknown';

  const meta = user.user_metadata || {};

  // 1. Editable team-facing display name (profile column or metadata)
  const display = rawDisplayName(user);
  if (display) return display;

  // 2. Provider full_name in metadata (Google supplies this)
  if (meta.full_name && meta.full_name.trim()) return meta.full_name.trim();

  // 3. first + last from metadata
  const metaFirst = (meta.first_name || '').trim();
  const metaLast = (meta.last_name || '').trim();
  if (metaFirst && metaLast) return `${metaFirst} ${metaLast}`;
  if (metaFirst) return metaFirst;

  // 4. first + last from top-level profile fields
  const profFirst = (user.first_name || '').trim();
  const profLast = (user.last_name || '').trim();
  if (profFirst && profLast) return `${profFirst} ${profLast}`;
  if (profFirst) return profFirst;

  // 5. Generic name/username fields
  if (user.name && user.name.trim()) return user.name.trim();

  // 6. Email
  if (user.email && user.email.trim()) return user.email.trim();

  // 7. Short UUID
  if (user.id) return shortId(user.id);

  return 'Unknown';
}

/**
 * Turn a full name into the compact "First L." form; single-word names pass
 * through unchanged.
 *
 * @param {string} fullName
 * @returns {string}
 */
function compactFromFullName(fullName) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

/**
 * Compact format: "First L." — first name + last initial.
 * Used for cards, lists, and creator labels where space is limited.
 *
 * Falls back to full display name when a last name/initial is not available,
 * and then to email and username per the standard chain.
 *
 * @param {object|null} user
 * @returns {string}
 */
export function formatUserCompact(user) {
  if (!user) return 'Unknown';

  const meta = user.user_metadata || {};

  // Prefer the editable display name, compacted
  const display = rawDisplayName(user);
  if (display) return compactFromFullName(display);

  // Provider full_name, compacted
  if (meta.full_name && meta.full_name.trim()) return compactFromFullName(meta.full_name);

  // Then metadata first/last
  const metaFirst = (meta.first_name || '').trim();
  const metaLast = (meta.last_name || '').trim();
  if (metaFirst && metaLast) return `${metaFirst} ${metaLast[0]}.`;
  if (metaFirst) return metaFirst;

  // Then profile fields
  const profFirst = (user.first_name || '').trim();
  const profLast = (user.last_name || '').trim();
  if (profFirst && profLast) return `${profFirst} ${profLast[0]}.`;
  if (profFirst) return profFirst;

  // Fall through to full display name (which will use email / id)
  return formatUserDisplayName(user);
}

/**
 * Retrieve and parse the current user from localStorage.
 * Returns a normalised user object with `displayName` and `compactName`
 * already resolved, or null if not found.
 *
 * @returns {{ id: string, email: string, displayName: string, compactName: string }|null}
 */
export function getCurrentUser() {
  try {
    const stored = localStorage.getItem('currentUser');
    if (!stored) return null;
    const user = JSON.parse(stored);
    return {
      ...user,
      displayName: formatUserDisplayName(user),
      compactName: formatUserCompact(user)
    };
  } catch {
    return null;
  }
}

/**
 * Short truncated ID for UUID-only values (last-resort fallback).
 * @param {string} id
 * @returns {string}
 */
export function shortId(id) {
  if (!id) return 'Unknown';
  if (typeof id !== 'string') return String(id);
  return id.length <= 10 ? id : `${id.slice(0, 8)}…`;
}

// ── Super-admin attribution ────────────────────────────────────────────────
//
// There is a single super-admin operator. When that operator seeds test data
// or fixes records inside a store they do NOT own (directly, or via the
// system-admin "join store" flow), the row's `created_by` stays pointed at the
// admin account. Surfacing the admin's real name — or a bare UUID, when RLS
// stops a customer from reading the admin's profile row — is confusing for that
// store's users, so we render those records with a neutral product label
// instead. Records the admin created inside their OWN stores keep their real
// name.
//
// The admin is matched by user id (not just email/app_role) so the relabel
// still fires when the profile row is unreadable. Mirrors the hardcoded
// SUPER_ADMIN_EMAILS list used elsewhere in the app — kept here in one place.
export const SUPER_ADMIN_USER_IDS = ['5023edee-2af2-470b-b82f-6f59bdce5cf9'];
export const SUPER_ADMIN_EMAILS = ['aj@chefsheet.com'];
export const COUNTERTOP_APP_CREATOR_LABEL = 'Countertop App';

/** True when the given user id belongs to a super admin. */
export function isSuperAdminId(id) {
  return !!id && SUPER_ADMIN_USER_IDS.includes(id);
}

// Cache of store_id → owned-by-super-admin?, so repeated surfaces don't refetch.
const _superAdminOwnedStoreCache = new Map();

/**
 * Resolve which of the given store ids are owned by a super admin.
 *
 * @param {object} supabase  Supabase client.
 * @param {Array<number|string>} storeIds
 * @returns {Promise<Set<number>>}  Set of store ids (as numbers) the super admin owns.
 */
export async function fetchSuperAdminOwnedStoreIds(supabase, storeIds) {
  const owned = new Set();
  const ids = [...new Set(
    (storeIds || [])
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v))
  )];
  if (!ids.length || !supabase) return owned;

  const toFetch = [];
  ids.forEach((id) => {
    if (_superAdminOwnedStoreCache.has(id)) {
      if (_superAdminOwnedStoreCache.get(id)) owned.add(id);
    } else {
      toFetch.push(id);
    }
  });

  if (toFetch.length) {
    try {
      const { data } = await supabase
        .from('stores')
        .select('id, owner_id')
        .in('id', toFetch);
      const ownerById = new Map((data || []).map((r) => [Number(r.id), isSuperAdminId(r.owner_id)]));
      toFetch.forEach((id) => {
        const isOwned = ownerById.get(id) === true;
        _superAdminOwnedStoreCache.set(id, isOwned);
        if (isOwned) owned.add(id);
      });
    } catch {
      // On failure leave these ids out of the owned set — the safer default is
      // to relabel admin-created records rather than leak the admin's name.
    }
  }
  return owned;
}

/** Convenience single-store form of {@link fetchSuperAdminOwnedStoreIds}. */
export async function isStoreOwnedBySuperAdmin(supabase, storeId) {
  if (storeId == null) return false;
  const owned = await fetchSuperAdminOwnedStoreIds(supabase, [storeId]);
  return owned.has(Number(storeId));
}

/**
 * Return the override creator label ("Countertop App") for a record created by
 * the super admin in a store they don't own, or null when no override applies
 * (use the normal name resolution in that case).
 *
 * @param {string} creatorId           Record's created_by user id.
 * @param {number|string} storeId      Record's store id.
 * @param {Set<number>} ownedStoreIds  Stores the super admin owns (from fetchSuperAdminOwnedStoreIds).
 * @returns {string|null}
 */
export function creatorOverrideLabel(creatorId, storeId, ownedStoreIds) {
  if (isSuperAdminId(creatorId) && !(ownedStoreIds && ownedStoreIds.has(Number(storeId)))) {
    return COUNTERTOP_APP_CREATOR_LABEL;
  }
  return null;
}

/**
 * Resolve a record's creator/accessor user id to a human-readable label.
 *
 * Resolution order:
 *   1. `override`      — an explicit label from {@link creatorOverrideLabel}
 *                        (the super admin acting in a store they don't own).
 *   2. `nameMap[id]`   — a real name already resolved from user_profiles.
 *   3. `currentUser`   — the signed-in user, when they are the creator.
 *   4. Countertop App  — an "outside" user: a record imported from the older
 *                        app whose `created_by` points at an auth account with no
 *                        readable user_profiles row (RLS hides users outside the
 *                        current store), or a deleted/system account. We never
 *                        surface the raw Supabase UUID (e.g. "19b9b3ea…") here —
 *                        a neutral product label reads better for the store.
 *
 * @param {string|null|undefined} id
 * @param {object} [opts]
 * @param {Map<string,string>|Record<string,string>} [opts.nameMap]  id → resolved name.
 * @param {{ id: string, compactName?: string, displayName?: string }|null} [opts.currentUser]
 * @param {string|null} [opts.override]  Explicit label that wins over everything else.
 * @returns {string|null}  The label, or null when `id` is empty so the caller can
 *                          supply its own default (e.g. "Unknown" / "Not yet").
 */
export function resolveCreatorLabel(id, { nameMap, currentUser, override } = {}) {
  if (!id) return null;
  if (override) return override;
  if (nameMap) {
    const mapped = nameMap instanceof Map ? nameMap.get(id) : nameMap[id];
    if (mapped) return mapped;
  }
  if (currentUser && currentUser.id === id) {
    return currentUser.compactName || currentUser.displayName || COUNTERTOP_APP_CREATOR_LABEL;
  }
  return COUNTERTOP_APP_CREATOR_LABEL;
}

/**
 * Update the currentUser localStorage blob with new name fields and dispatch
 * a 'userProfileUpdated' CustomEvent so any live UI can react immediately.
 *
 * @param {{ display_name?: string, first_name?: string, last_name?: string }} nameFields
 */
export function persistUserNameToLocalStorage({ display_name, first_name, last_name }) {
  try {
    const stored = localStorage.getItem('currentUser');
    if (!stored) return;
    const user = JSON.parse(stored);

    // Update user_metadata (what Supabase auth.updateUser writes)
    user.user_metadata = user.user_metadata || {};
    if (display_name !== undefined) user.user_metadata.display_name = display_name;
    if (first_name !== undefined) user.user_metadata.first_name = first_name;
    if (last_name !== undefined) user.user_metadata.last_name = last_name;

    // Also update any top-level profile fields that legacy code reads
    if (display_name !== undefined) user.display_name = display_name;
    if (first_name !== undefined) user.first_name = first_name;
    if (last_name !== undefined) user.last_name = last_name;

    localStorage.setItem('currentUser', JSON.stringify(user));

    // Notify any listening components (sidebar, etc.)
    window.dispatchEvent(new CustomEvent('userProfileUpdated', { detail: { user } }));
  } catch {
    // Silently ignore — the DB save already succeeded
  }
}
